/**
 * Mobile Bottom Tab Bar — Replaces sidebar on mobile.
 * Minimalist line-art icons with neon glow for active state.
 */
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Calendar,
  ClipboardList,
  Activity,
  BarChart3,
  Brain,
} from 'lucide-react';

const tabs = [
  { path: '/today', labelKey: 'nav.today', icon: LayoutDashboard },
  { path: '/schedule', labelKey: 'nav.schedule', icon: Calendar },
  { path: '/plan', labelKey: 'nav.plan', icon: ClipboardList },
  { path: '/history', labelKey: 'nav.history', icon: Activity },
  { path: '/insights', labelKey: 'nav.insights', icon: BarChart3 },
  { path: '/coach', labelKey: 'nav.coach', icon: Brain },
];

export function MobileBottomNav() {
  const location = useLocation();
  const { t } = useTranslation();

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-white/[0.04] pb-[env(safe-area-inset-bottom,0px)]"
      style={{
        background: 'rgba(11, 14, 20, 0.8)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
      }}
    >
      <div className="flex items-center justify-around h-14">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = location.pathname === tab.path;

          return (
            <Link
              key={tab.path}
              to={tab.path}
              className={cn(
                'flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-all duration-200',
                'active:scale-95',
              )}
            >
              <div className="relative">
                {/* Static teal glow behind active icon */}
                {isActive && (
                  <div
                    className="absolute inset-0 -m-1.5 rounded-full"
                    style={{
                      boxShadow: '0 0 10px rgba(0, 255, 200, 0.25), 0 0 4px rgba(0, 255, 200, 0.15)',
                    }}
                  />
                )}
                <Icon
                  className="relative h-5 w-5 transition-all duration-200"
                  style={{
                    color: isActive ? 'rgba(0, 255, 200, 0.8)' : 'rgba(100, 116, 139, 0.5)',
                  }}
                  strokeWidth={isActive ? 2 : 1.2}
                />
              </div>
              <span
                className="text-[8px] font-mono tracking-[0.1em] uppercase transition-colors duration-200"
                style={{
                  color: isActive ? 'rgba(0, 255, 200, 0.7)' : 'rgba(100, 116, 139, 0.4)',
                }}
              >
                {t(tab.labelKey)}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
